/**
 * copilotActivity — accesseurs purs de la ligne d'activité éphémère, du bilan de réponse et du compteur de
 * jetons du copilote (UPT-2041, lot 1 ; contrat de référence : `rest/SPEC-UPT-2041.md` §4).
 *
 * Deux événements SSE structurés alimentent ce module :
 *
 *  - `activity` : `{ seq, phase: thinking|reading|preparing|acting|answering, tool?, entity?, entityLabel?,
 *    entityLabelPlural?, count?, status: started|done|error, durationMs?, elapsedMs }`. Émis à chaque étape de la
 *    boucle d'outils ; `count` n'arrive qu'avec le `done` d'une lecture. Il ne porte JAMAIS une valeur de donnée
 *    (ni argument ni résultat) : seulement outil, entité, libellés, compte et durées.
 *  - `usage` (avant `done`) : `{ promptTokens, completionTokens, totalTokens, source: provider|estimate, calls,
 *    toolCalls, durationMs, conversationTotalTokens, monthTokensUsed, monthTokenBudget? }`.
 *
 * Règles tenues ici :
 *  - L'activité est éphémère : l'état `ActivityState` n'est jamais persisté ; seul le bilan (`EntrySummary`) reste
 *    en mémoire de session sous la réponse.
 *  - Le nom brut de l'outil (`tool`) sert à apparier un `done`/`error` à son `started` et, sans entité, à choisir
 *    le libellé de repli (outil Géode `geode_*` → « données Géode », autre nom → « outil inconnu ») : il n'est
 *    jamais affiché. L'entité s'affiche par son libellé de catalogue, sinon par son code ; l'entité
 *    `documentation` par un libellé i18n.
 *  - Les données du flux sont non fiables : l'analyse est défensive (null si illisible, champ invalide ignoré).
 *  - Aucune clé de traduction composée : chaque accesseur renvoie des clés littérales d'une liste fermée (la règle
 *    i18n interdit les gabarits de clé dans le JSX). Fonctions pures, sans React ni react-intl : le tiroir passe
 *    une fermeture `t(id, values)`.
 */

export type ActivityPhase = 'thinking' | 'reading' | 'preparing' | 'acting' | 'answering';
export type ActivityStatus = 'started' | 'done' | 'error';
export type UsageSource = 'provider' | 'estimate';

/** Traducteur injecté (react-intl : `(id, values) => intl.formatMessage({ id }, values)`). */
export type ActivityTranslator = (id: string, values?: Record<string, string | number>) => string;

/** Une étape vue dans le flux `activity` (jamais d'argument ni de valeur). */
export interface ActivityItem {
  seq: number;
  phase: ActivityPhase;
  /** Nom brut de l'outil : appariement started → done/error SEULEMENT, jamais affiché. */
  tool?: string;
  /** Code d'entité (`bcmdossier`) ; `documentation` pour les outils de documentation. */
  entity?: string;
  entityLabel?: string;
  entityLabelPlural?: string;
  count?: number;
  status: ActivityStatus;
  durationMs?: number;
}

/** Activité du tour en cours : en mémoire seulement, jamais persistée ni rechargée. */
export interface ActivityState {
  startedAt: number;
  items: ActivityItem[];
  /** L'étape en cours (dernier événement, tant qu'il est `started`). */
  current?: ActivityItem;
}

/** Événement `usage` validé. */
export interface StreamUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  source: UsageSource;
  calls: number;
  toolCalls: number;
  durationMs: number;
  conversationTotalTokens: number;
  /** Omis par le serveur si la lecture du mois a échoué (avec `monthTokenBudget`). */
  monthTokensUsed?: number;
  /** Omis si le budget est illimité ou si la lecture du mois a échoué. */
  monthTokenBudget?: number;
}

/** Bilan porté par l'entrée de réponse (en mémoire de session). */
export interface EntrySummary {
  toolCalls: number;
  durationMs: number;
  tokens?: { prompt: number; completion: number; total: number; source: UsageSource };
  items: ActivityItem[];
  /** Réponse interrompue par le bouton Stop : bilan partiel. */
  interrupted?: boolean;
}

/** Résultat de `summarize` : compteurs par nature d'étape, durée et jetons (null = inconnu). */
export interface ActivitySummary {
  reads: number;
  preparations: number;
  actions: number;
  /** Étapes d'outil en erreur (sous-ensemble de reads + preparations + actions). */
  errors: number;
  durationMs: number | null;
  tokens: number | null;
  estimated: boolean;
}

export interface ActivitySummaryParts {
  steps: string;
  duration: string;
  tokens: string;
  estimated: boolean;
}

const PHASES: readonly ActivityPhase[] = ['thinking', 'reading', 'preparing', 'acting', 'answering'];
const STATUSES: readonly ActivityStatus[] = ['started', 'done', 'error'];
const MAX_ITEMS = 200;
const MAX_LABEL_LENGTH = 120;
const MAX_CODE_LENGTH = 80;

/** Part de la consommation mensuelle à partir de laquelle l'avertissement apparaît (spec §5.4). */
export const BUDGET_WARNING_RATIO = 0.9;
/** Nombre d'étapes terminées gardées sous le verbe courant. */
export const VISIBLE_FINISHED_STEPS = 3;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const nonNegative = (value: unknown): number | undefined => {
  const n = finiteNumber(value);
  return n !== undefined && n >= 0 ? n : undefined;
};

const text = (value: unknown, max: number): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed.slice(0, max);
};

/** Une étape d'outil (lecture, préparation, action) par opposition à « je réfléchis » / « je rédige ». */
export const isToolPhase = (phase: ActivityPhase): boolean =>
  phase === 'reading' || phase === 'preparing' || phase === 'acting';

// ---------------------------------------------------------------------------------------------------------------
// Analyse défensive des événements
// ---------------------------------------------------------------------------------------------------------------

/** Analyse la donnée d'un événement `activity` ; null si illisible ou si la phase / l'état sont inconnus. */
export function parseActivityEvent(raw: string | null | undefined): ActivityItem | null {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  const phase = PHASES.find((candidate) => candidate === parsed.phase);
  const status = STATUSES.find((candidate) => candidate === parsed.status);
  const seq = finiteNumber(parsed.seq);
  if (!phase || !status || seq === undefined) {
    return null;
  }
  const tool = text(parsed.tool, MAX_CODE_LENGTH);
  const entity = text(parsed.entity, MAX_CODE_LENGTH);
  const entityLabel = text(parsed.entityLabel, MAX_LABEL_LENGTH);
  const entityLabelPlural = text(parsed.entityLabelPlural, MAX_LABEL_LENGTH);
  const count = nonNegative(parsed.count);
  const durationMs = nonNegative(parsed.durationMs);
  return {
    seq,
    phase,
    status,
    ...(tool !== undefined ? { tool } : {}),
    ...(entity !== undefined ? { entity } : {}),
    ...(entityLabel !== undefined ? { entityLabel } : {}),
    ...(entityLabelPlural !== undefined ? { entityLabelPlural } : {}),
    ...(count !== undefined ? { count: Math.floor(count) } : {}),
    ...(durationMs !== undefined ? { durationMs } : {}),
  };
}

/**
 * Analyse la donnée d'un événement `usage` ; null si le total de jetons est illisible. Une `source` absente ou
 * inconnue vaut `estimate` : on n'affiche jamais un chiffre comme mesuré sans que le serveur l'ait dit.
 */
export function parseUsageEvent(raw: string | null | undefined): StreamUsage | null {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  const prompt = nonNegative(parsed.promptTokens);
  const completion = nonNegative(parsed.completionTokens);
  const total = nonNegative(parsed.totalTokens);
  if (prompt === undefined && completion === undefined && total === undefined) {
    return null;
  }
  const promptTokens = prompt ?? 0;
  const completionTokens = completion ?? 0;
  const totalTokens = total ?? (promptTokens + completionTokens);
  const budget = nonNegative(parsed.monthTokenBudget);
  const monthUsed = nonNegative(parsed.monthTokensUsed);
  return {
    promptTokens,
    completionTokens,
    totalTokens,
    source: parsed.source === 'provider' ? 'provider' : 'estimate',
    calls: nonNegative(parsed.calls) ?? 0,
    toolCalls: nonNegative(parsed.toolCalls) ?? 0,
    durationMs: nonNegative(parsed.durationMs) ?? 0,
    conversationTotalTokens: nonNegative(parsed.conversationTotalTokens) ?? totalTokens,
    ...(monthUsed !== undefined ? { monthTokensUsed: monthUsed } : {}),
    ...(budget !== undefined && budget > 0 ? { monthTokenBudget: budget } : {}),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// État de l'activité (réducteur pur)
// ---------------------------------------------------------------------------------------------------------------

export const startActivity = (now: number): ActivityState => ({ startedAt: now, items: [] });

/**
 * Cherche l'étape existante à laquelle l'événement entrant se rattache. Le serveur numérote CHAQUE événement
 * (`seq` croît de 1 à chaque `activity`) : un `done` / `error` se rattache donc à la dernière étape encore
 * `started` de la même phase et du même outil (les outils s'exécutent l'un après l'autre ; `tool` est omis pour
 * un nom hors catalogue, auquel cas les deux côtés sont vides). Un même `seq` reçu deux fois est aussi fusionné
 * (rejeu, serveur qui réutiliserait le numéro).
 */
function findMergeIndex(items: readonly ActivityItem[], incoming: ActivityItem): number {
  const bySeq = items.findIndex((item) => item.seq === incoming.seq);
  if (bySeq >= 0) {
    return bySeq;
  }
  if (incoming.status !== 'started') {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      const item = items[index];
      if (item.status === 'started' && item.phase === incoming.phase && item.tool === incoming.tool) {
        return index;
      }
    }
  }
  return -1;
}

/** Applique un événement `activity` : fusionne dans l'étape existante ou en ajoute une. Ne modifie pas l'entrée. */
export function applyActivity(state: ActivityState, incoming: ActivityItem): ActivityState {
  const items = state.items.slice();
  const index = findMergeIndex(items, incoming);
  let merged: ActivityItem;
  if (index >= 0) {
    merged = { ...items[index], ...incoming, seq: items[index].seq };
    items[index] = merged;
  } else {
    merged = incoming;
    items.push(incoming);
    if (items.length > MAX_ITEMS) {
      items.shift();
    }
  }
  return {
    startedAt: state.startedAt,
    items,
    current: merged.status === 'started' ? merged : undefined,
  };
}

/** Les dernières étapes d'outil terminées (réussies ou en erreur), de la plus ancienne à la plus récente. */
export function finishedSteps(items: readonly ActivityItem[], limit: number = VISIBLE_FINISHED_STEPS): ActivityItem[] {
  return items.filter((item) => isToolPhase(item.phase) && item.status !== 'started').slice(-limit);
}

// ---------------------------------------------------------------------------------------------------------------
// Verbes et libellés
// ---------------------------------------------------------------------------------------------------------------

/**
 * Clé du verbe pour une phase et un état. `started` = présent (« Je lis… »), `done` = passé (« J'ai lu… »),
 * `error` = échec. « Je réfléchis… » et « Je rédige la réponse… » n'ont qu'une forme.
 */
export function activityVerbKey(phase: ActivityPhase, status: ActivityStatus): string {
  switch (phase) {
    case 'reading':
      if (status === 'done') return 'copilot.activity.readingDone';
      if (status === 'error') return 'copilot.activity.readingError';
      return 'copilot.activity.reading';
    case 'preparing':
      if (status === 'done') return 'copilot.activity.preparingDone';
      if (status === 'error') return 'copilot.activity.preparingError';
      return 'copilot.activity.preparing';
    case 'acting':
      if (status === 'done') return 'copilot.activity.actingDone';
      if (status === 'error') return 'copilot.activity.actingError';
      return 'copilot.activity.acting';
    case 'answering':
      return 'copilot.activity.answering';
    case 'thinking':
    default:
      return 'copilot.activity.thinking';
  }
}

/** Préfixe des outils du catalogue Géode côté serveur (`geode_records_list`, `geode_record_get`…). */
const GEODE_TOOL_PREFIX = 'geode_';

/** Vrai pour un outil du catalogue Géode ; faux pour un autre nom, ou quand le serveur n'en envoie pas (hors catalogue). */
export const isGeodeTool = (tool: string | undefined): boolean =>
  typeof tool === 'string' && tool.startsWith(GEODE_TOOL_PREFIX);

/**
 * Libellé de l'entité visée : documentation → i18n, pluriel du catalogue si `count` > 1, repli sur le code. Sans
 * entité : un outil Géode (`geode_*`) garde « données Géode » ; tout autre outil (nom étranger ou absent) est un
 * « outil inconnu » — on ne prétend pas lire des données Géode quand on ignore ce que fait l'outil.
 */
function entityText(item: ActivityItem, t: ActivityTranslator): string {
  if (item.entity === 'documentation') {
    return t('copilot.activity.entity.documentation');
  }
  if ((item.count ?? 0) > 1 && item.entityLabelPlural) {
    return item.entityLabelPlural;
  }
  const known = item.entityLabel ?? item.entity;
  if (known !== undefined) {
    return known;
  }
  return isGeodeTool(item.tool) ? t('copilot.activity.entity.unknown') : t('copilot.activity.tool.unknown');
}

/**
 * Phrase d'une étape (« Je parcours « Dossiers BCM » (6)… », « J'ai lu « Dossier BCM » », « Lecture impossible : … »).
 * `kind` aiguille le message ICU : `many` (compte > 1), `none` (compte 0), `one` (1 ou inconnu).
 */
export function activityLabel(item: ActivityItem, t: ActivityTranslator): string {
  const count = item.count;
  let kind: 'many' | 'none' | 'one' = 'one';
  if (count !== undefined && count > 1) {
    kind = 'many';
  } else if (count === 0) {
    kind = 'none';
  }
  return t(activityVerbKey(item.phase, item.status), {
    entity: entityText(item, t),
    count: count ?? 0,
    kind,
  });
}

/**
 * Verbe courant du tour : la phrase de l'étape en cours, « Réflexion… » entre deux étapes, et le repli historique
 * (`copilot.thinking`) tant qu'aucun événement `activity` n'est arrivé (ancien serveur). C'est le texte que la ligne
 * d'activité affiche et que la région vocale du tiroir annonce.
 */
export function activityVerb(state: ActivityState, t: ActivityTranslator): string {
  if (state.items.length === 0) {
    return t('copilot.thinking');
  }
  return state.current ? activityLabel(state.current, t) : t('copilot.activity.thinking');
}

// ---------------------------------------------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------------------------------------------

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = numberFormats.get(key);
  if (!format) {
    try {
      format = new Intl.NumberFormat(locale, options);
    } catch {
      // locale ou option non prise en charge : repli sur l'anglais sans style d'unité fragile
      format = new Intl.NumberFormat('en', options);
    }
    numberFormats.set(key, format);
  }
  return format;
}

const seconds = (value: number, locale: string, fractionDigits: number): string =>
  numberFormat(locale, {
    style: 'unit', unit: 'second', unitDisplay: 'short', maximumFractionDigits: fractionDigits,
  }).format(value);

const minutes = (value: number, locale: string): string =>
  numberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'short', maximumFractionDigits: 0 }).format(value);

/**
 * Durée lisible, dans la langue de l'interface : « 0,8 s », « 4 s », « 4,1 s », « 12 s », « 1 min 12 s ».
 * Sous 10 s, une décimale (0 si elle est nulle) ; au-delà, des secondes entières ; au-delà d'une minute, minutes
 * et secondes.
 */
export function formatDuration(ms: number, locale: string): string {
  const safe = Number.isFinite(ms) && ms > 0 ? ms : 0;
  if (safe < 10_000) {
    return seconds(Math.round(safe / 100) / 10, locale, 1);
  }
  const total = Math.round(safe / 1000);
  if (total < 60) {
    return seconds(total, locale, 0);
  }
  const wholeMinutes = Math.floor(total / 60);
  const rest = total % 60;
  return rest === 0
    ? minutes(wholeMinutes, locale)
    : `${minutes(wholeMinutes, locale)} ${seconds(rest, locale, 0)}`;
}

/** Nombre de jetons avec le séparateur de milliers de la langue (« 2 520 », « 2,520 », « 2.520 »). */
export function formatTokens(n: number, locale: string): string {
  const safe = Number.isFinite(n) ? Math.round(n) : 0;
  return numberFormat(locale, { maximumFractionDigits: 0 }).format(safe);
}

/**
 * Locale de l'interface. La déclaration locale de react-intl (app/types/react-intl/index.d.ts) type `locale`
 * `unknown` : on la lit défensivement (même garde que AssessmentFormPage).
 */
export const localeOf = (intl: Record<string, unknown>): string =>
  typeof intl.locale === 'string' && intl.locale !== '' ? intl.locale : 'en';

// ---------------------------------------------------------------------------------------------------------------
// Bilan
// ---------------------------------------------------------------------------------------------------------------

/** Le sous-ensemble de `usage` dont le bilan a besoin (un `StreamUsage` complet convient). */
export interface UsageLike {
  totalTokens?: number;
  source?: UsageSource;
  durationMs?: number;
}

/**
 * Compte les étapes d'outil par nature (lectures, préparations, actions) et résume durée et jetons.
 * `durationMs` : celle du serveur (`usage.durationMs`), sinon `fallbackDurationMs` (mesure locale), sinon null.
 * `tokens` : null si `usage` est absent (jetons inconnus) ; `estimated` si le fournisseur n'a pas mesuré.
 */
export function summarize(
  items: readonly ActivityItem[],
  usage?: UsageLike | null,
  fallbackDurationMs?: number,
): ActivitySummary {
  const tools = items.filter((item) => isToolPhase(item.phase));
  const serverDuration = nonNegative(usage?.durationMs);
  const localDuration = nonNegative(fallbackDurationMs);
  return {
    reads: tools.filter((item) => item.phase === 'reading').length,
    preparations: tools.filter((item) => item.phase === 'preparing').length,
    actions: tools.filter((item) => item.phase === 'acting').length,
    errors: tools.filter((item) => item.status === 'error').length,
    durationMs: serverDuration !== undefined && serverDuration > 0 ? serverDuration : (localDuration ?? null),
    tokens: nonNegative(usage?.totalTokens) ?? null,
    estimated: usage?.source === 'estimate',
  };
}

/**
 * Bilan à ranger sous la réponse. `undefined` quand il n'y a rien à dire (ancien serveur : ni activité ni
 * `usage`), sauf pour une réponse interrompue, qui a toujours un bilan partiel.
 */
export function buildEntrySummary(
  items: readonly ActivityItem[],
  usage: StreamUsage | null,
  localDurationMs: number,
  interrupted: boolean = false,
): EntrySummary | undefined {
  if (!interrupted && items.length === 0 && !usage) {
    return undefined;
  }
  const toolItems = items.filter((item) => isToolPhase(item.phase));
  const localDuration = Math.max(0, Math.round(localDurationMs));
  return {
    toolCalls: usage ? usage.toolCalls : toolItems.length,
    durationMs: usage && usage.durationMs > 0 ? usage.durationMs : localDuration,
    ...(usage
      ? {
        tokens: {
          prompt: usage.promptTokens,
          completion: usage.completionTokens,
          total: usage.totalTokens,
          source: usage.source,
        },
      }
      : {}),
    items: items.slice(),
    ...(interrupted ? { interrupted: true } : {}),
  };
}

/** `summarize` appliqué au bilan rangé sous une réponse. */
export function summarizeEntry(entry: EntrySummary): ActivitySummary {
  return summarize(
    entry.items,
    entry.tokens ? { totalTokens: entry.tokens.total, source: entry.tokens.source } : null,
    entry.durationMs,
  );
}

/**
 * Les trois morceaux du bilan : étapes (« 2 lectures Géode, 1 préparation »), durée (« 4,1 s »), jetons
 * (« ≈ 2 520 jetons » si estimé). Chaîne vide pour un morceau inconnu ou sans objet.
 */
export function summaryParts(summary: ActivitySummary, t: ActivityTranslator, locale: string): ActivitySummaryParts {
  const steps: string[] = [];
  if (summary.reads > 0) steps.push(t('copilot.summary.reads', { count: summary.reads }));
  if (summary.preparations > 0) steps.push(t('copilot.summary.preparations', { count: summary.preparations }));
  if (summary.actions > 0) steps.push(t('copilot.summary.actions', { count: summary.actions }));
  const tokens = summary.tokens === null
    ? ''
    : t('copilot.summary.tokens', {
      count: summary.tokens,
      tokens: `${summary.estimated ? '≈\u00a0' : ''}${formatTokens(summary.tokens, locale)}`,
    });
  return {
    // « 2 lectures Géode, 1 préparation » : la virgule de la langue vient de la traduction (ar : « ، »).
    steps: steps.reduce((joined, step) => (joined === '' ? step : t('copilot.summary.join', { a: joined, b: step })), ''),
    duration: summary.durationMs === null ? '' : formatDuration(summary.durationMs, locale),
    tokens,
    estimated: summary.estimated && summary.tokens !== null,
  };
}

/** Bilan en une ligne : « 2 lectures Géode · 4,1 s · 2 520 jetons ». */
export function summaryText(summary: ActivitySummary, t: ActivityTranslator, locale: string): string {
  const parts = summaryParts(summary, t, locale);
  return [parts.steps, parts.duration, parts.tokens].filter((part) => part !== '').join(' · ');
}

// ---------------------------------------------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------------------------------------------

/** Vrai quand un budget mensuel existe et que sa consommation atteint 90 % (spec §5.4). */
export function isBudgetNearlyExhausted(
  usage: Pick<StreamUsage, 'monthTokensUsed' | 'monthTokenBudget'> | null | undefined,
): boolean {
  if (!usage || usage.monthTokensUsed === undefined || usage.monthTokenBudget === undefined
    || !(usage.monthTokenBudget > 0)) {
    return false;
  }
  return usage.monthTokensUsed / usage.monthTokenBudget >= BUDGET_WARNING_RATIO;
}
